import * as THREE from 'three';
import { OrbMaterial } from './orb/OrbMaterial';
import { PortalLightMaterial } from './PortalLight';
import { settings } from '@/config';
import { createLogger } from '@/utils/logger';
import type { MaterialManager } from './MaterialManager';

const log = createLogger('materials');

/**
 * Live handles for the ORVAE scene's registered materials, for the debug GUI.
 *
 * The architecture is now a **baked-lighting** workflow (2026-07-11): every static mesh
 * wears the unlit `MeshBasicMaterial` for the Blender-baked atlas its collection was
 * baked into (`public/textures/Baked_Images/Scene_0N/Baked_M_S_N.webp`). Only the three
 * custom systems stay procedural/dynamic — grass (handled by {@link Grass}), water
 * ({@link WaterFX}) and the emblem ({@link EmblemMaterial}).
 */
export interface OrvaeMaterialControls {
  /** One unlit baked material per atlas key (`S1_1`, `S1_2`, … `S4_3`; 12 total). */
  baked: Record<string, THREE.MeshBasicMaterial>;
  /** Neutral material any UNMAPPED mesh falls back to (logged to the console). */
  fallback: THREE.MeshBasicMaterial;
  /** The amber energy orb material worn by the `Orave_emble_S_3` sphere. */
  emblem: OrbMaterial;
  /** One alpha-cutout material per vegetation plane texture (`bush`/`cypress`/`plant`/`tree`). */
  planes: Record<string, THREE.MeshBasicMaterial>;
  /** Push `settings.planes.transform[key]` (offset/scale/rotation/flipY) onto that texture. */
  applyPlaneTransform(key: string): void;
  /** Shared glow material worn by every `Bright_Light_White_S_*` portal light. */
  portalLight: PortalLightMaterial;
  /** Live per-mesh UV-set control for every dual-UV baked mesh (the debug GUI exposes a
   *  "use uv1" checkbox per name). The Blender bake landed on `uv0` on some meshes and `uv1`
   *  on others with no consistent rule, so the correct set is decided VISUALLY at runtime
   *  rather than guessed at build time. See {@link UvControls}. */
  uv: UvControls;
  /** Debug: paint every baked mesh a UNIQUE flat colour (by name) to prove the material is
   *  actually reaching it. If a mesh turns a colour, the resolver hit it (so any wrongness is
   *  UV/atlas); if it stays white/blank, the material never applied. Logs a name→colour table. */
  setDebugColors(on: boolean): void;
  /** Live atlas-image switcher for keys that ship alternate bakes (e.g. S1_2 has 3 versions).
   *  Lets the debug GUI flip a material's texture between variants in real time. */
  atlasVariants: AtlasVariants;
}

/** Live per-key atlas-image switcher (see {@link OrvaeMaterialControls.atlasVariants}). */
export interface AtlasVariants {
  /** Atlas key → available variant labels (one dropdown per key). */
  options: Record<string, string[]>;
  /** Current variant label per key. */
  state: Record<string, string>;
  /** Swap the atlas key's material `map` to the named variant (lazy-loads on first use). */
  set(key: string, label: string): void;
}

/** Live UV-set toggle for the dual-UV baked meshes (see {@link OrvaeMaterialControls.uv}). */
export interface UvControls {
  /** Unique Blender node names that carry a second UV set (one GUI row each). */
  names: string[];
  /** Current per-name choice: `true` = sample `uv1`, `false` = `uv0`. */
  state: Record<string, boolean>;
  /** Point every leaf mesh of `name` at `uv0` or `uv1` and repaint immediately. */
  set(name: string, useUv1: boolean): void;
}

/**
 * Atlas key → webp path. TWELVE atlases (2026-07-17 model `scene.glb`): TWO for
 * Scene 1, FOUR for Scene 2, THREE each for Scenes 3 & 4.
 *
 * CHANGED vs the previous export: the STAIRS were re-baked out of Scene 1 into Scene 2's
 * collection — Scene 1's `Baked_3_S_1`/`Baked_4_S_1` are gone and Scene 2 gained
 * `Baked_3_S_2` (the staircase treads) + `Baked_4_S_2` (the curved stair stringers).
 * Verified by opening the atlases themselves. That also matches `World.SCENE_OVERRIDES`,
 * which already treats `Stairs`/`Stairs_Pillar` as Scene 2 (they're the 1→2 staircase).
 */
const BAKED_PATHS: Record<string, string> = {
  S1_1: '/textures/Baked_Images/Scene_01/Baked_1_S_1.webp',
  S1_2: '/textures/Baked_Images/Scene_01/Baked_2_S_1.webp',
  S2_1: '/textures/Baked_Images/Scene_02/Baked_1_S_2.webp',
  S2_2: '/textures/Baked_Images/Scene_02/Baked_2_S_2.webp',
  S2_3: '/textures/Baked_Images/Scene_02/Baked_3_S_2.webp',
  S2_4: '/textures/Baked_Images/Scene_02/Baked_4_S_2.webp',
  S3_1: '/textures/Baked_Images/Scene_03/Baked_1_S_3.webp',
  S3_2: '/textures/Baked_Images/Scene_03/Baked_2_S_3.webp',
  S3_3: '/textures/Baked_Images/Scene_03/Baked_3_S_3.webp',
  S4_1: '/textures/Baked_Images/Scene_04/Baked_1_S_4.webp',
  S4_2: '/textures/Baked_Images/Scene_04/Baked_2_S_4.webp',
  S4_3: '/textures/Baked_Images/Scene_04/Baked_3_S_4.webp',
};

/**
 * Vegetation BILLBOARD PLANES → their own image texture (2026-07-17). These are NOT part of
 * any baked atlas: each is a flat plane wearing one cut-out foliage image from
 * `public/textures/`, matched by the Blender mesh name. The regexes are ANCHORED, so
 * `Cypress_Tree_1_S_3` can never be caught by the plain `Tree_*` rule.
 */
const PLANE_TEXTURES: ReadonlyArray<{ key: string; test: RegExp; path: string }> = [
  // ALL SIX cypress trees wear the sunlit image (2026-07-22, user: "4,5,6 also use sunlight
  // only texture remove that normal one"). The earlier `Cypress_Tree_Without_Sunlight.webp`
  // variant for 4/5/6 was removed the same day.
  { key: 'cypress', test: /^Cypress_Tree_\d+_S_\d+$/i, path: '/textures/Cypress_Tree.webp' },
  { key: 'tree', test: /^Tree_\d+_S_\d+$/i, path: '/textures/Tree.webp' },
  { key: 'bush', test: /^Bush_\d+_S_\d+$/i, path: '/textures/Bush.webp' },
  { key: 'plant', test: /^Plant_\d+_S_\d+$/i, path: '/textures/Plant.webp' },
];

/**
 * Alternate atlas images for a key, switchable live from the debug GUI. Add entries here to
 * compare multiple bakes for one atlas key (FIRST entry = the default already loaded via
 * {@link BAKED_PATHS}; the rest lazy-load on first selection; paths with spaces are
 * `encodeURI`'d at load time). Empty = no variant dropdown. (Scene 1's temporary 3-way
 * `Baked_2_S_1` comparison was removed 2026-07-13 at the user's request.)
 */
const BAKED_VARIANTS: Record<string, Array<{ label: string; path: string }>> = {};

/**
 * Explicit mesh-name → atlas-key map, transcribed from the Blender outliner (the four
 * scene collection screenshots the user provided). Each `Baked_M_S_N` collection was
 * baked to ONE atlas, so a mesh's atlas is decided by which collection it lives in — a
 * relationship the flattened GLB no longer carries, hence this table.
 *
 * Keyed by the CLEAN node name (Blender's `.001` duplicate suffix stripped). TWO entries
 * are keyed by the RAW name instead, because they collide with a clean-name sibling that
 * lives in a DIFFERENT atlas — the resolver tries the raw name first (see
 * {@link bakedKeyForName}):
 *   - `Stage_S_4.001` (Baked_2_S_4 → S4_2) vs the mass `Stage_S_4.002..096` (Baked_1_S_4 →
 *     S4_1): all clean to `Stage_S_4`, so `.001` needs its own raw entry and the clean
 *     `Stage_S_4` catches the rest.
 *   - `3.005` (a door part in Baked_2_S_1 → S1_2) cleans to `3`, which the arch-voussoir
 *     regex would otherwise claim for `Baked_1_S_1` (S1_1). Raw entry wins.
 * The numbered arch voussoirs `1..35` and their `_E` twins (Scene 1's `Baked_1_S_1`) are
 * matched by regex, not listed here.
 *
 * Current for `scene.glb` (2026-07-17). Verified by simulating this resolver over every mesh
 * node in the GLB: 145/145 resolve, 0 fall through to the neutral fallback. Re-run that check
 * after any re-export — a silent fallback reads as "the texture didn't apply".
 *
 * Ongoing gotcha: the user's Blender collections get reorganised between exports, so atlas
 * membership moves. When it's unclear which atlas a mesh belongs to, OPEN THE ATLAS WEBP with
 * the Read tool — the islands are recognisable (that's how the stairs were traced from Scene 1
 * to Scene 2's `Baked_3_S_2`/`Baked_4_S_2`).
 */
const NAME_TO_KEY: Record<string, string> = {
  // ---- Scene 1 ------------------------------------------------------------------
  // Baked_2_S_1 — pillars, the double door + its parts, main stage stone.
  Stage_Stone_S_1: 'S1_2',
  Pillar_Support_S_1: 'S1_2',
  Stone_Pillar: 'S1_2',
  Door_Main_Pillar_S_1: 'S1_2',
  Stage_Pillar_S_1: 'S1_2',
  Pillar_Base_S_1: 'S1_2',
  Pillar_base_top_S_1: 'S1_2',
  Left_Door: 'S1_2',
  Right_Door: 'S1_2',
  'Medieval Double Door 9': 'S1_2',
  // Door part. ABSENT from the 2026-07-17 model, but kept as a cheap guard: if it ever
  // returns, this raw entry stops the arch regex claiming it (it cleans to `3`).
  '3.005': 'S1_2',

  // ---- Scene 2 ------------------------------------------------------------------
  // Baked_1_S_2
  Pillar_2_S_2: 'S2_1',
  Pillar_S_2: 'S2_1',
  Stair_Connector_S_2: 'S2_1',
  // Baked_2_S_2
  Stage_Pillar_S_2: 'S2_2',
  Stage_Stone_S_2: 'S2_2',
  Stair_Connector_Pillar_S2: 'S2_2',
  // Baked_3_S_2 — the staircase treads. MOVED here from Scene 1's old `Baked_3_S_1`
  // (2026-07-17 re-bake): `Stairs` is the Scene 1→2 staircase, so it now lives in Scene 2's
  // collection, matching how `World.SCENE_OVERRIDES` already classified it.
  Stairs: 'S2_3',
  // Baked_4_S_2 — the curved stair stringers/supports (was Scene 1's `Baked_4_S_1`).
  Stairs_Pillar: 'S2_4',

  // ---- Scene 3 ------------------------------------------------------------------
  // Baked_1_S_3 — stage tiles only now.
  Stage_Tiles_S_3: 'S3_1',
  // Baked_2_S_3 — bridge stone, the baked "compound" grass tufts, stage + rings.
  Bridge_Stone_S_3: 'S3_2',
  Grass_Compund_01_S_3: 'S3_2',
  Grass_Compund_02_S_3: 'S3_2',
  Grass_Compund_03_S_3: 'S3_2',
  Grass_Compund_04_S_3: 'S3_2',
  Grass_Compund_05_S_3: 'S3_2',
  Grass_Compund_06_S_3: 'S3_2',
  Grass_Compund_07_S_3: 'S3_2',
  Grass_Compund_08_S_3: 'S3_2',
  Grass_Compund_09_S_3: 'S3_2',
  Stage__S_3: 'S3_2',
  Stage_Pillar_S_3: 'S3_2',
  Stage_Pillar_Stone_S_3: 'S3_2',
  Stone_Ring_S_3: 'S3_2',
  // Baked_3_S_3 — the 47 bridge tiles (`Bridge_Tile_S_3.001..047`, all clean to this).
  Bridge_Tile_S_3: 'S3_3',

  // ---- Scene 4 ------------------------------------------------------------------
  // Baked_1_S_4 — the big tiled stage. JOINED IN BLENDER (2026-07-17): what used to be ~95
  // `Stage_S_4.002..096` + 9 `Stage_Tiles_01_S_4.001..009` separate meshes is now just these
  // two. (The old clean `Stage_S_4` catch-all entry is gone with that mass — `Stage_S_4.001`
  // below still raw-matches to S4_2.)
  Stage_Tiles_01_S_4: 'S4_1',
  Stage_Tiles_02_S_4: 'S4_1',
  // Baked_2_S_4 — `Stage_S_4.001` (raw-matched to escape the `Stage_S_4` mass above),
  // round stone, the second stage stone, the OTHER tiles.
  'Stage_S_4.001': 'S4_2',
  Round_Stone_S_4: 'S4_2',
  Stage_Stone_02_S_4: 'S4_2',
  Stage_Tiles_S_4: 'S4_2',
  // Baked_3_S_4 — the pillars (their OWN atlas now).
  Pillar_S_4: 'S4_3',
  Pillar_Support_S_4: 'S4_3',
};

const cleanName = (raw: string): string => raw.replace(/\.\d+$/, '');

/**
 * EVERY dual-UV baked mesh in this GLB bakes to its SECOND UV set (`TEXCOORD_1`/`uv1`); the
 * first set (`uv0`) is a stray original/lightmap unwrap. Verified across arch stone `12` and
 * every stage/door piece (`Stage_Pillar_S_2`, `Stage_Stone_S_2`, `Stage__S_3`, `Stage_Tiles_S_3`,
 * `Stage_Tiles_01_S_4`, `Stage_Stone_02_S_4`, `Left_Door`) via the live UV toggle. So the default
 * is simply: if a baked mesh has a `uv1`, sample it (`MeshBasicMaterial.map` reads channel 0, so
 * `uv` is repointed at `uv1`). The earlier belief that some baked to `uv0` was a MISDIAGNOSIS —
 * those meshes (`Stage_S_4`/`.001`/`Stage__S_3`) were the MULTI-PRIMITIVE ones getting NO material
 * at all (the `rawName` bug, fixed in `Model.sanitizeNames`), nothing to do with UV choice.
 *
 * `USE_UV0_NAMES` lists the exceptions that must stay on `uv0`. The debug GUI can still override
 * any mesh live. Matched against the raw AND the clean name.
 *
 * UPDATED 2026-07-17 from the user's own GUI pass (screenshot of "UV set fix"): every dual-UV
 * mesh was ticked to uv1 EXCEPT the two joined Scene 4 tile meshes, which the user unticked —
 * so they bake to uv0 and now live here as permanent exceptions. Before this they defaulted to
 * uv1 and had to be re-unticked by hand on every reload. These two are exactly the meshes that
 * were JOINED in Blender for this export, which is a plausible reason their UV order differs
 * from everything else.
 */
const USE_UV0_NAMES = new Set<string>(['Stage_Tiles_01_S_4', 'Stage_Tiles_02_S_4']);

/** Resolve a mesh's baked atlas key, or null if it isn't a baked mesh. */
function bakedKeyForName(rawName: string): string | null {
  // RAW name first — this is what disambiguates the collisions: `Stage_S_4.001` (S4_2) from
  // the `Stage_S_4.002..096` mass (S4_1), and the door part `3.005` (S1_2) from arch stone
  // `3` (S1_1). Must precede the arch regex, since `cleanName('3.005') === '3'` would match it.
  if (rawName in NAME_TO_KEY) return NAME_TO_KEY[rawName]!;
  const clean = cleanName(rawName);
  // Scene 1 arch voussoirs (`1`..`35`) + their emissive twins (`1_E`..`35_E`).
  if (/^\d+(_E)?$/i.test(clean)) return 'S1_1';
  return NAME_TO_KEY[clean] ?? null;
}

/** Resolve a vegetation billboard plane's texture key, or null if it isn't one. */
function planeKeyForName(rawName: string): string | null {
  const clean = cleanName(rawName);
  for (const entry of PLANE_TEXTURES) {
    if (entry.test.test(clean)) return entry.key;
  }
  return null;
}

/** Custom (non-baked) meshes handled by their own systems. */
export const isWaterName = (name: string): boolean => /water/i.test(name);
export const isEmblemName = (name: string): boolean => /orave_emble/i.test(name);
/**
 * The glowing portal light meshes — see {@link PortalLightMaterial}.
 *
 * In the 2026-07-17 `scene.glb` the user renamed these from `Bright_Light_White_S_1`/`_S_2` to
 * Blender's default `Plane`/`Plane.001`. Both spellings are matched so either export works.
 * The `Plane*` pair is identified as the lights on strong evidence, not just the name: they are
 * the only bare `Plane` nodes, they're flat 4-vertex quads, and they sit at exactly the previous
 * `Bright_Light_White_S_*` positions (≈[0, 3.2, -3] by the Scene 1 door and ≈[4.7, 25, -110] in
 * Scene 2). Without this they'd hit the neutral fallback and show up as beige quads.
 */
export const isPortalLightName = (name: string): boolean => {
  const c = cleanName(name);
  return /^Bright_Light_White_S_\d+$/i.test(c) || c === 'Plane';
};
/** A vegetation billboard plane (`Bush_*`, `Cypress_Tree_*`, `Plant_*`, `Tree_*`). */
export const isPlaneTextureName = (name: string): boolean => planeKeyForName(name) !== null;
/** The custom animated-grass source meshes (NOT the baked `Grass_Compund_*` tufts). */
export const isCustomGrassName = (name: string): boolean => {
  const c = cleanName(name);
  return c === 'Grass_S_2' || /^Grass_\d+_S_3$/.test(c) || c === 'Stage_Grass_S_4';
};

/**
 * Registers the ORVAE scene's materials and installs the name-based resolver.
 *
 * Baked static architecture → the correct unlit atlas per {@link NAME_TO_KEY}. Custom
 * meshes (water / grass / emblem) get lightweight placeholders here and are REPLACED by
 * their real systems in `World` (the water/grass source meshes are then hidden). The
 * resolver reads `mesh.userData.rawName` (set by {@link Model.sanitizeNames}) so it can
 * still tell apart nodes whose clean names collide.
 */
export function registerOrvaeMaterials(
  materials: MaterialManager,
  /**
   * The app's SHARED `LoadingManager` (2026-07-19, user: "make sure in preloading only you're
   * loading all the assets").
   *
   * This used to be `new THREE.TextureLoader()` with no manager, which silently binds to three's
   * DEFAULT LoadingManager — so the 12 baked atlases and every foliage texture loaded OUTSIDE the
   * preloader's accounting. The preloader reported "done" while the scene's actual surfaces were
   * still arriving, which is why the architecture popped in untextured after the gate. Passing the
   * shared manager makes `AssetManager.onProgress` count them and hold the preloader until they
   * are genuinely ready.
   */
  manager?: THREE.LoadingManager,
): OrvaeMaterialControls {
  const loader = new THREE.TextureLoader(manager);

  // One unlit baked material per atlas. `MeshBasicMaterial` = the light is IN the
  // texture; `prepareTexture` applies the GLB baked-workflow rules (flipY=false, sRGB,
  // max anisotropy). Colour left white so the atlas shows exactly as baked.
  const baked: Record<string, THREE.MeshBasicMaterial> = {};
  const variantCache: Record<string, THREE.Texture> = {}; // path → loaded texture (for variants)
  for (const [key, path] of Object.entries(BAKED_PATHS)) {
    const tex = loader.load(
      path,
      undefined,
      undefined,
      () => log.warn(`Baked atlas failed to load: ${path}`),
    );
    materials.prepareTexture(tex, true);
    variantCache[path] = tex; // so switching back to the default reuses this texture
    // `toneMapped: false` is essential for a baked workflow — the light is already IN the
    // atlas, so it must reach the screen 1:1 as exported from Blender. Left tone-mapped,
    // the renderer's ACES Filmic curve would darken the mids and desaturate the highlights,
    // which is exactly the "not applied perfectly / doesn't match Blender" look. The map is
    // sampled sRGB→linear and the material writes straight to the sRGB output, unaltered.
    // `DoubleSide`: some baked meshes are exported wound inside-out (e.g. the big Scene 4
    // stage `Stage_S_4.001` — every camera-facing face is a BACKFACE, so with the default
    // `FrontSide` it culls to nothing and reads as "mesh not visible"). These materials are
    // UNLIT baked colour (no normal-dependent shading), so rendering both faces is free of
    // any lighting artefact and just guarantees the surface always shows. 2026-07-12.
    const mat = new THREE.MeshBasicMaterial({ map: tex, toneMapped: false, side: THREE.DoubleSide });
    baked[key] = mat;
    materials.register(`orvae:baked:${key}`, mat);
  }

  const fallback = new THREE.MeshBasicMaterial({ color: '#b7a88f', toneMapped: false, side: THREE.DoubleSide });
  materials.register('orvae:fallback', fallback);

  // Custom-system placeholders (the meshes wearing these are hidden + replaced in World).
  const waterPlaceholder = new THREE.MeshBasicMaterial({ color: '#86a6b6' });
  const grassPlaceholder = new THREE.MeshBasicMaterial({ color: '#3f5a2a' });
  const emblem = new OrbMaterial();
  materials.register('orvae:water', waterPlaceholder);
  materials.register('orvae:grass', grassPlaceholder);
  materials.register('orvae:emblem', emblem);

  // Vegetation billboard planes: one unlit CUT-OUT material per foliage image — the exact
  // equivalent of Blender's "Alpha Clip" blend mode, which is the look being matched.
  //
  // `transparent: FALSE` + `alphaTest` is deliberate (2026-07-17). alphaTest DISCARDS any texel
  // below the threshold, so the leaf shape is punched out and the plane's rectangle never shows.
  // Leaving `transparent: true` on as well (as this did originally) pushed the planes into the
  // sorted TRANSPARENT queue, where they're drawn after — and sorted against — the cloud
  // sprites; a plane could then paint over a nearer cloud. As a pure cutout they sit in the
  // OPAQUE queue instead: they draw first, write depth only where a leaf actually is, and the
  // clouds behind then depth-test correctly through the gaps. No sorting, no flicker.
  // (All four webp files were verified to carry a real alpha channel — VP8X alpha flag set.)
  const planes: Record<string, THREE.MeshBasicMaterial> = {};
  for (const entry of PLANE_TEXTURES) {
    const tex = loader.load(
      entry.path,
      undefined,
      undefined,
      () => log.warn(`Plane texture failed to load: ${entry.path}`),
    );
    materials.prepareTexture(tex, true);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: false,
      alphaTest: settings.planes.alphaTest,
      side: THREE.DoubleSide, // a single plane must read from both sides
      toneMapped: false, // match the baked atlases so foliage sits in the same colour space
    });
    // Clamp (not repeat): a cut-out foliage image must never tile if it's scaled/offset — the
    // transparent border just extends, which `alphaTest` then discards.
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.center.set(0.5, 0.5); // rotate/scale about the image centre, not its corner
    planes[entry.key] = mat;
    materials.register(`orvae:plane:${entry.key}`, mat);
  }

  /** Live UV transform per foliage texture (see `settings.planes.transform`). */
  function applyPlaneTransform(key: string): void {
    const cfg = settings.planes.transform[key];
    const tex = planes[key]?.map;
    if (!cfg || !tex) return;
    tex.offset.set(cfg.offsetX, cfg.offsetY);
    tex.repeat.set(cfg.repeatX, cfg.repeatY);
    tex.rotation = THREE.MathUtils.degToRad(cfg.rotationDeg);
    if (tex.flipY !== cfg.flipY) {
      tex.flipY = cfg.flipY;
      tex.needsUpdate = true; // flipY only takes effect on re-upload
    }
  }
  for (const key of Object.keys(planes)) applyPlaneTransform(key);

  // The glowing portal lights (`Bright_Light_White_S_*`). One shared material; World adds the
  // additive halo shell per mesh (see `World.setupPortalLights`).
  const portalLight = new PortalLightMaterial();
  materials.register('orvae:portalLight', portalLight);


  const unmapped = new Set<string>();

  // Every dual-UV baked leaf mesh, grouped by Blender node name, with BOTH UV attributes
  // stashed so the debug GUI can flip a mesh between `uv0`/`uv1` live (the bake landed on a
  // different set per mesh, no consistent rule — so it's dialled in visually, not guessed).
  const uvLeaves = new Map<string, Array<{ geo: THREE.BufferGeometry; uv0: THREE.BufferAttribute; uv1: THREE.BufferAttribute }>>();
  const uvState: Record<string, boolean> = {};

  // Every baked leaf mesh + its resolved atlas key — used by the random-colour debug test.
  const bakedMeshes: Array<{ mesh: THREE.Mesh; name: string; key: string }> = [];

  materials.addResolver((mesh, name) => {
    const raw = (mesh.userData.rawName as string | undefined) ?? name;

    if (isWaterName(raw)) return waterPlaceholder; // hidden by World.setupWater
    if (isEmblemName(raw)) return emblem;
    if (isCustomGrassName(raw)) return grassPlaceholder; // hidden by World.setupGrass
    if (isPortalLightName(raw)) return portalLight; // World adds the additive halo shell on top

    // Vegetation billboard planes wear their own foliage image, not a baked atlas.
    const planeKey = planeKeyForName(raw);
    if (planeKey) return planes[planeKey]!;

    const key = bakedKeyForName(raw);
    if (key) {
      bakedMeshes.push({ mesh, name: cleanName(raw), key });
      // The Blender bake landed on `uv0` for some meshes and `uv1` for others (arch stone
      // `12`, several stage/door pieces) — no reliable build-time rule, so capture BOTH UV
      // sets for any dual-UV mesh and let the debug GUI toggle live. `MeshBasicMaterial.map`
      // always samples channel 0 (`uv`), so "use uv1" means copying the `uv1` attribute onto
      // `uv`. USE_UV1_NAMES seeds the initial choice from the best visual guess so far.
      const clean = cleanName(raw);
      const uv1 = mesh.geometry.getAttribute('uv1') as THREE.BufferAttribute | undefined;
      const uv0 = mesh.geometry.getAttribute('uv') as THREE.BufferAttribute | undefined;
      if (uv1 && uv0) {
        let list = uvLeaves.get(clean);
        if (!list) {
          list = [];
          uvLeaves.set(clean, list);
        }
        list.push({ geo: mesh.geometry, uv0, uv1 });
        const useUv1 = !(USE_UV0_NAMES.has(raw) || USE_UV0_NAMES.has(clean));
        uvState[clean] = useUv1;
        if (useUv1) mesh.geometry.setAttribute('uv', uv1);
      }
      return baked[key]!;
    }

    if (!unmapped.has(raw)) {
      unmapped.add(raw);
      log.warn(`No baked atlas mapped for mesh "${raw}" — using the neutral fallback.`);
    }
    return fallback;
  });

  const uv: UvControls = {
    get names() {
      return [...uvLeaves.keys()].sort();
    },
    state: uvState,
    set(nodeName: string, useUv1: boolean): void {
      const list = uvLeaves.get(nodeName);
      if (!list) return;
      uvState[nodeName] = useUv1;
      for (const { geo, uv0, uv1 } of list) geo.setAttribute('uv', useUv1 ? uv1 : uv0);
    },
  };

  // Random-colour debug test: swap every baked mesh's atlas material for a unique flat colour
  // (stable per name) so it's obvious which meshes ARE getting a material and which stay blank.
  let debugColorMats: THREE.MeshBasicMaterial[] = [];
  let debugActive = false;
  function setDebugColors(on: boolean): void {
    if (on === debugActive) return;
    debugActive = on;
    if (on) {
      const logged = new Set<string>();
      for (const { mesh, name, key } of bakedMeshes) {
        mesh.userData.origBakedMat = mesh.material;
        const color = new THREE.Color().setHSL(hashHue(name), 0.7, 0.5);
        const mat = new THREE.MeshBasicMaterial({ color, toneMapped: false, side: THREE.DoubleSide });
        debugColorMats.push(mat);
        mesh.material = mat;
        if (!logged.has(name)) {
          logged.add(name);
          log.info(`[debug colour] ${name} (${key}) → #${color.getHexString()}`);
        }
      }
    } else {
      for (const { mesh } of bakedMeshes) {
        if (mesh.userData.origBakedMat) mesh.material = mesh.userData.origBakedMat as THREE.Material;
      }
      for (const m of debugColorMats) m.dispose();
      debugColorMats = [];
    }
  }

  // Live atlas-image switcher: flip a key's material `map` between its variants (lazy-load).
  const atlasOptions: Record<string, string[]> = {};
  const atlasState: Record<string, string> = {};
  for (const [key, variants] of Object.entries(BAKED_VARIANTS)) {
    if (!baked[key]) continue;
    atlasOptions[key] = variants.map((v) => v.label);
    atlasState[key] = variants[0]!.label; // default = the already-loaded first entry
  }
  const atlasVariants: AtlasVariants = {
    options: atlasOptions,
    state: atlasState,
    set(key: string, label: string): void {
      const variants = BAKED_VARIANTS[key];
      const mat = baked[key];
      if (!variants || !mat) return;
      const variant = variants.find((v) => v.label === label);
      if (!variant) return;
      atlasState[key] = label;
      let tex = variantCache[variant.path];
      if (!tex) {
        // `encodeURI` so the space in "Baked_2_S_1 2.png" becomes a valid URL.
        tex = loader.load(
          encodeURI(variant.path),
          undefined,
          undefined,
          () => log.warn(`Atlas variant failed to load: ${variant.path}`),
        );
        materials.prepareTexture(tex, true);
        variantCache[variant.path] = tex;
      }
      mat.map = tex;
      mat.needsUpdate = true;
    },
  };

  return {
    baked,
    fallback,
    emblem,
    planes,
    applyPlaneTransform,
    portalLight,
    uv,
    setDebugColors,
    atlasVariants,
  };
}

/** Deterministic 0..1 hue from a mesh name, so a given mesh always gets the same debug colour. */
function hashHue(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return (h % 360) / 360;
}
